import * as Logger from './logger.js';
import * as Utils from './utils.js';
import { SlickGrid, SlickDataView, Formatters } from 'slickgrid';

/**
 * Shared SlickGrid utility functions for consistent grid setup and management
 */
export class SlickGridUtils {
    // Canonical implementations live in utils.js (docs/XSS_HARDENING_PLAN.md
    // Tier C) - re-exported here since callers already depend on this class.
    static escapeHtml(value) {
        return Utils.escapeHtml(value);
    }

    static escapeHtmlAttribute(value) {
        return Utils.escapeHtmlAttribute(value);
    }

    static tryPrettifyJson(value) {
        const raw = String(value || '');
        const trimmed = raw.trim();
        if (!trimmed) {
            return { text: '', isJson: false };
        }

        const maybeJson =
            (trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'));
        if (!maybeJson) {
            return { text: raw, isJson: false };
        }

        try {
            const parsed = JSON.parse(trimmed);
            return {
                text: JSON.stringify(parsed, null, 2),
                isJson: true,
            };
        } catch (_err) {
            return { text: raw, isJson: false };
        }
    }

    static ansiToStyledHtml(value) {
        const text = String(value || '');
        if (!text) {
            return '';
        }

        const ansiColorMap = {
            30: '#000000', 31: '#ff4d4f', 32: '#43a047', 33: '#f9a825',
            34: '#1976d2', 35: '#8e24aa', 36: '#00838f', 37: '#d9d9d9',
            90: '#8c8c8c', 91: '#ff8a80', 92: '#81c784', 93: '#ffe082',
            94: '#90caf9', 95: '#ce93d8', 96: '#80deea', 97: '#ffffff',
        };

        let fg = null;
        let bg = null;
        let bold = false;
        let html = '';
        let cursor = 0;

        const appendChunk = (chunk) => {
            if (!chunk) {
                return;
            }
            const escaped = SlickGridUtils.escapeHtml(chunk);
            const styles = [];
            if (fg) {
                styles.push(`color:${fg}`);
            }
            if (bg) {
                styles.push(`background-color:${bg}`);
            }
            if (bold) {
                styles.push('font-weight:700');
            }

            if (styles.length > 0) {
                html += `<span style="${styles.join(';')}">${escaped}</span>`;
            } else {
                html += escaped;
            }
        };

        while (cursor < text.length) {
            const escStart = text.indexOf('\u001b[', cursor);
            if (escStart < 0) {
                appendChunk(text.slice(cursor));
                break;
            }

            appendChunk(text.slice(cursor, escStart));

            const mIndex = text.indexOf('m', escStart + 2);
            if (mIndex < 0) {
                appendChunk(text.slice(escStart));
                break;
            }

            const codeSection = text.slice(escStart + 2, mIndex);
            cursor = mIndex + 1;

            const rawCodes = (codeSection || '0').split(';').filter(x => x.length > 0);
            const codes = rawCodes.length > 0 ? rawCodes : [ '0' ];

            for (const codeText of codes) {
                const code = Number(codeText);
                if (!Number.isFinite(code)) {
                    continue;
                }

                if (code === 0) {
                    fg = null;
                    bg = null;
                    bold = false;
                } else if (code === 1) {
                    bold = true;
                } else if (code === 22) {
                    bold = false;
                } else if (code === 39) {
                    fg = null;
                } else if (code === 49) {
                    bg = null;
                } else if ((code >= 30 && code <= 37) || (code >= 90 && code <= 97)) {
                    fg = ansiColorMap[code] || fg;
                } else if ((code >= 40 && code <= 47) || (code >= 100 && code <= 107)) {
                    const fgCode = code - 10;
                    bg = ansiColorMap[fgCode] || bg;
                }
            }
        }

        return html;
    }

    static buildMessageTooltipHtml(rawMessage, options = {}) {
        const raw = String(rawMessage || '');
        const preferPretty = options.preferPretty !== false;
        const { text, isJson } = SlickGridUtils.tryPrettifyJson(raw);
        const selectedText = (isJson && preferPretty) ? text : raw;
        const contentHtml = SlickGridUtils.ansiToStyledHtml(selectedText);
        const copyPayload = SlickGridUtils.escapeHtmlAttribute(selectedText);
        const metaBadge = isJson ? '<span class="hs-log-tooltip-badge">JSON</span>' : '';
        const metaInfo = `<span class="hs-log-tooltip-meta">${raw.length} chars</span>`;
        const toggleBtn = isJson
            ? `<button type="button" class="hs-log-tooltip-btn" data-tooltip-action="toggle-view">${preferPretty ? 'Show Raw' : 'Show Pretty JSON'}</button>`
            : '';
        const body = contentHtml || '<span class="hs-log-tooltip-empty">(empty message)</span>';

        return [
            '<div class="hs-log-tooltip-header">',
            metaBadge,
            metaInfo,
            '<span class="hs-log-tooltip-spacer"></span>',
            toggleBtn,
            '<button type="button" class="hs-log-tooltip-btn" data-tooltip-action="copy">Copy</button>',
            '</div>',
            `<pre class="hs-log-tooltip-content" data-copy-text="${copyPayload}">${body}</pre>`,
        ].join('');
    }

    /**
     * Initialize a SlickGrid instance with standard configuration
     */
    static initGrid(parentElement, dataView, columns, options = {}) {
        const defaultOptions = {
            enableCellNavigation: false,
            enableTextSelectionOnCells: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            multiColumnSort: true,
            enableAutoSizeColumns: true,
            rowHeight: 32,
            ...options,
        };

        const grid = new SlickGrid(parentElement, dataView, columns, defaultOptions);
        Logger.debug('Initialized SlickGrid with columns:', columns.length);
        return grid;
    }

    /**
     * Create a standard data view for SlickGrid
     */
    static createDataView() {
        return new SlickDataView();
    }

    /**
     * Format ANSI-colored text in SlickGrid cell
     * Converts ANSI escape sequences to inline styles
     */
    static ansiFormatter(row, cell, value, columnDef, dataContext) {
        if (!value) return '';
        
        const text = String(value);
        const ansiColorMap = {
            '30': '#000000', '31': '#ff0000', '32': '#00ff00', '33': '#ffff00',
            '34': '#0000ff', '35': '#ff00ff', '36': '#00ffff', '37': '#ffffff',
            '90': '#808080', '91': '#ff8080', '92': '#80ff80', '93': '#ffff80',
            '94': '#8080ff', '95': '#ff80ff', '96': '#80ffff', '97': '#ffffff',
        };

        let html = '';
        let i = 0;
        let currentColor = null;
        let currentBg = null;
        let currentBold = false;

        while (i < text.length) {
            if (text[i] === '\u001b' && text[i + 1] === '[') {
                // Found ANSI sequence
                let j = i + 2;
                let code = '';
                while (j < text.length && text[j] !== 'm') {
                    code += text[j];
                    j++;
                }
                j++; // skip 'm'

                // Parse the code
                if (code === '0' || code === '') {
                    currentColor = null;
                    currentBg = null;
                    currentBold = false;
                } else if (code === '1') {
                    currentBold = true;
                } else if (code === '39') {
                    currentColor = null;
                } else if (code === '49') {
                    currentBg = null;
                } else {
                    const colorCode = code.replace(/^(10|4)/, '');
                    if (ansiColorMap[colorCode]) {
                        if (code.startsWith('4')) {
                            currentBg = ansiColorMap[colorCode];
                        } else if (code.startsWith('10')) {
                            currentBg = ansiColorMap[colorCode];
                        } else {
                            currentColor = ansiColorMap[colorCode];
                        }
                    }
                }

                i = j;
            } else {
                // Regular character
                let charText = text[i];
                if (charText === '<') charText = '&lt;';
                if (charText === '>') charText = '&gt;';
                if (charText === '&') charText = '&amp;';

                const style = [];
                if (currentColor) style.push(`color:${currentColor}`);
                if (currentBg) style.push(`background-color:${currentBg}`);
                if (currentBold) style.push('font-weight:bold');

                if (style.length > 0) {
                    html += `<span style="${style.join(';')}">${charText}</span>`;
                } else {
                    html += charText;
                }

                i++;
            }
        }

        return `<pre style="margin:0;font-family:monospace;white-space:pre-wrap;word-wrap:break-word;">${html}</pre>`;
    }

    /**
     * Severity badge formatter
     */
    static severityFormatter(row, cell, value, columnDef, dataContext) {
        if (!value) return '';
        const severityColors = {
            'debug': '#888',
            'info': '#0066cc',
            'warn': '#ff9900',
            'warning': '#ff9900',
            'error': '#cc0000',
            'critical': '#990000',
            'severe': '#990000',
        };
        const color = severityColors[value.toLowerCase()] || '#ccc';
        return `<span style="display:inline-block;padding:2px 6px;background-color:${color};color:white;border-radius:3px;font-weight:bold;font-size:0.85em;">${value}</span>`;
    }

    /**
     * Timestamp formatter
     */
    static timestampFormatter(row, cell, value, columnDef, dataContext) {
        if (!value) return '';
        try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                const locale = navigator.language || 'en-US';
                return date.toLocaleString(locale);
            }
        } catch (err) {
            Logger.warn('Invalid timestamp:', value);
        }
        return String(value);
    }

    /**
     * Ellipsis truncation formatter for long text
     */
    static ellipsisFormatter(row, cell, value, columnDef, dataContext) {
        if (!value) return '';
        const maxLen = columnDef.maxLength || 100;
        const text = String(value);
        if (text.length > maxLen) {
            return `<span title="${text.replace(/"/g, '&quot;')}">${text.substring(0, maxLen)}...</span>`;
        }
        return text;
    }

    static logMessagePreviewFormatter(row, cell, value, columnDef, dataContext) {
        const raw = String(dataContext?.message || value || '');
        const plain = String(value || raw || '');
        const compact = plain.replace(/\s+/g, ' ').trim();
        const maxLen = columnDef.maxLength || 160;
        const preview = compact.length > maxLen ? `${compact.substring(0, maxLen)}...` : compact;
        const safeMessage = SlickGridUtils.escapeHtmlAttribute(raw);
        const safePreview = SlickGridUtils.escapeHtml(preview || '(empty)');
        return `<span class="hs-log-message-preview" data-log-message="${safeMessage}">${safePreview}</span>`;
    }

    /**
     * Setup common event handlers for grid
     */
    static setupGridEvents(grid, dataView, onSort, onClick) {
        if (onSort) {
            grid.onSort.subscribe((e, args) => {
                // In single-column sort mode (default) SlickGrid fires onSort with
                // top-level sortCol/sortAsc and NO sortCols array. Multi-column mode
                // provides sortCols. Normalize to always give the callback a sortCols array.
                const sortCols = args.sortCols ||
                    (args.sortCol ? [{ sortCol: args.sortCol, sortAsc: args.sortAsc }] : []);
                onSort(args.sortAsc, sortCols);
            });
        }

        if (onClick) {
            grid.onClick.subscribe((e, args) => {
                onClick(args.row, args.cell, args.e);
            });
        }

        // Row count change
        dataView.onRowCountChanged.subscribe((ev, args) => {
            grid.updateRowCount();
            grid.render();
        });

        // Rows changed
        dataView.onRowsChanged.subscribe((ev, args) => {
            grid.invalidateRows(args.rows);
            grid.render();
        });
    }

    /**
     * Add rows to grid (updates existing or adds new)
     */
    static addRows(dataView, rows) {
        const existing = dataView.getItems();
        const existingIds = new Set(existing.map(r => r.id));

        rows.forEach(row => {
            const idx = existing.findIndex(r => r.id === row.id);
            if (idx !== -1) {
                // Update existing row
                Object.assign(existing[idx], row);
                dataView.updateItem(idx, existing[idx]);
            } else {
                // Add new row
                dataView.addItem(row);
            }
        });
    }

    /**
     * Clear all rows from grid
     */
    static clearRows(dataView) {
        dataView.setItems([]);
    }

    /**
     * Get all rows from grid
     */
    static getRows(dataView) {
        return dataView.getItems();
    }

    /**
     * Apply filter to grid
     */
    static applyFilter(dataView, filterFunc) {
        dataView.setFilter(filterFunc);
    }
}

export default SlickGridUtils;
